import { Crop } from '@crops';
import { Box } from '@mui/system';
import { ChartData } from 'chart.js';
import { useCallback, useEffect, useState } from 'react';
import { Control, useForm, useWatch } from 'react-hook-form';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import pannelState from 'state/rightPannelState';
import CropApi from 'api/CropApi';
import AiChartWithGrid from './AiChartWithGrid';
import { CommonDataState } from 'state/commonState';
import AiListGrid from './AiListGrid';
import { TPaging } from '@metadata';
import EtcUtils from 'packages/ui/src/libs/EtcUtils';
import AiTransitionTab from './AiTransitionTab';

type Props = {
  control: Control<Crop.FormData, any>;
  className: string;
};

const AiReportContainer = ({ control, className }: Props) => {
  const [pannel, setPannel] = useRecoilState(pannelState);

  const [localFormData, setLocalFormData] = useState<Crop.FormData>();
  const [gridData, setGridData] = useState<Array<any>>([]);
  const [detailList, setDetailList] = useState<Crop.TAiListResponse>({
    data: [],
    total: {
      total: 0,
      totalarea: 0,
    },
  });
  const [chartData, setChartData] = useState<ChartData<'doughnut', any, any>>({
    labels: [],
    datasets: [],
  });

  const formData = useWatch<Crop.FormData>({ control });

  const [paging, setPaging] = useState<TPaging>({
    pageSize: 10,
    pageNo: 0,
  });

  useEffect(() => {}, []);

  useEffect(() => {
    if (!localFormData) return;
    fetchData();
  }, [localFormData]);

  useEffect(() => {
    if (!localFormData) return;
    setLocalFormData((prev) => ({ ...prev, pageNo: paging.pageNo, pageSize: paging.pageSize }));
  }, [paging]);

  useEffect(() => {
    if (pannel.status.isOpen && pannel.status.currentPage === 'ai') {
      // console.log('AI 분석결과 탭 opend');
      setLocalFormData({ ...formData, pageNo: 0, pageSize: 10 });
    } else if (pannel.status.currentPage === 'culture') {
      // console.log('AI 분석결과 탭 closed');
      setLocalFormData(null);
    }
  }, [pannel.status.isOpen, pannel.status.currentPage]);



  const fetchData = useCallback(async () => {
    setPannel((prev) => ({ ...prev, status: { ...prev.status, isLoading: true } }));
    try {
      // 최초 페이지 활성화됐을 시점의 검색조건을 가져오고 그 이후에는, submit(조회버튼)전까지
      // 그렇지 않으면 조회를 누르지 않았더라도 왼쪽 select box 및 input box의 값에 실시간으로 영향을 받게 됨.
      const param = localFormData || formData;
      console.log('fetchData : ', param);
      const aiStatistics = await CropApi.AiStatistics(param);
      const aiDetailList = await CropApi.AiDetailList({
        pageNo: localFormData.pageNo || 0,
        pageSize: localFormData.pageSize || 10,
        searchAddr: formData.searchAddr,
        treeKindCd: formData.treeKindCd,
        ctpvCd: formData.ctpvCd,
        sggCd: formData.sggCd,
        emdCd: formData.emdCd,
      });

      gridDetailProcessing(aiDetailList);
      chartProcessing(aiStatistics);
      gridProcessing(aiStatistics);
    } catch (e) {
      console.error(e);
    } finally {
      setPannel((prev) => ({ ...prev, status: { ...prev.status, isLoading: false } }));
    }
  }, [localFormData, formData, pannel.status.currentDetId]);

  const chartProcessing = (_data) => {
    if (_data.length < 1) {
      setChartData((prev) => ({ ...prev, labels: [], datasets: [] }));
      return;
    }
    const colors = _data.map((i) => `#${i.color}`);
    const _labels = _data.map((i) => i.tree_kind_nm);
    setChartData((prev) => ({
      ...prev,
      labels: _labels,
      datasets: [{ backgroundColor: colors, data: _data }],
    }));
  };

  const gridProcessing = (_data) => {
    /* 작물 목록 */
    setGridData(_data);
  };

  const gridDetailProcessing = (_data) => {
    /* 상세 목록 */
    console.log('gridDetailProcessing : ', _data);
    setDetailList(_data);
    setPannel((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        aiDetailList: {
          ...prev.data.aiDetailList,
          total: _data.total,
        },
      },
    }));
  };

  const excelDownload = useCallback(async () => {
    if (!localFormData) return;
    setPannel((prev) => ({ ...prev, status: { ...prev.status, isLoading: true } }));
    const response = await CropApi.AiListExcel({
      ctpvCd: localFormData.ctpvCd,
      sggCd: localFormData.sggCd,
      emdCd: localFormData.emdCd,
      treeKindCd: localFormData.treeKindCd,
      searchAddr: localFormData.searchAddr,
    });
    EtcUtils.download(response);
    setPannel((prev) => ({ ...prev, status: { ...prev.status, isLoading: false } }));
  }, [localFormData]);

  return (
    <div className={className}>
    
      {!pannel.status.aiTransition.isOpen ? (
        <Box className="micMainArea">
          {/* ai분석결과 ( 도넛 차트 + 표 ) */}
          {pannel.status.searchMode === 'area' && (
            <AiChartWithGrid
              chartData={chartData}
              gridData={gridData}
            />
          )}
          {/* 상세 목록 -신고내역 */}
          <AiListGrid
            excelDownload={excelDownload}
            detailList={detailList}
            setPaging={setPaging}
            paging={paging}
            control={control}
          />
        </Box>
      ) : null}
      {pannel.status.aiTransition.isOpen ? <AiTransitionTab control={control} /> : null}
    </div>
  );
};

export default AiReportContainer;
